import { Actions, AppContext, Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { useApi, useGlobalContext } from "@hooks";
import useI18n from "@i18n";
import { Api } from "@Shared/schemas";
import { PlaylistResponse } from "@Shared/schemas/playlists/playlistSchemas";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMount } from "react-use";

let completeTimeout: any = null;

interface IUsePlaylistData {
  id: number;
}

interface PerformanceStats {
  awareness_score: number;
  scan_rate: number;
  scan_timing: number;
  total_score: number; // Decision score
  critical_scan_score: number;
  byb_iq: number;
}

// Define the structure of the playlist results response
interface PlaylistSession {
  status: string;
  play_session: {
    id: number;
    type: string;
    game_mode: string;
    play_position: string | null;
    experience_gained: number;
    is_completed: boolean;
    user_rating: number | null;
    game_speed: number | null;
    created_at: string;
    updated_at: string | null;
  };
  results: {
    labels_made: number | null;
    labels_succeeded: number | null;
    receivals_made: number | null;
    receivals_succeeded: number | null;
    tackles_made: number | null;
    tackles_succeeded: number | null;
    shots_made: number | null;
    shots_succeeded: number | null;
    passes_made: number | null;
    passes_succeeded: number | null;
    passes_score: number | null;
    passes_packing_rate: number | null;
    awareness_score: number | null;
    total_score: number | null;
    scan_score: number | null;
    scan_rate: number | null;
    scan_timing: number | null;
    critical_scan_score: number | null;
    critical_scans: number | null;
    byb_iq: number | null;
  };
  scenarios: Array<{
    scenario_id: number;
    scenario_doc_id: string;
  }>;
}

interface PlaylistResultsResponse {
  playlist_id: number;
  created_at: string | null;
  playlist_sessions: PlaylistSession[];
}

const usePlaylistData = ({ id }: IUsePlaylistData) => {
  const { state, dispatch } = useContext(AppContext);
  const { globalState } = useGlobalContext();
  const navigate = useNavigate();
  const { i18n } = useI18n();

  const [loading, setLoading] = useState(true);
  const [playlist, setPlaylist] = useState<PlaylistResponse>();
  const [bestResult, setBestResult] = useState<any | null>(null);
  const [currentStats, setCurrentStats] = useState<PerformanceStats | null>(null);
  const [previousStats, setPreviousStats] = useState<PerformanceStats | null>(null);

  const { call: getPlaylists } = useApi<typeof Api.Playlists.GetPlaylists>({
    api: Api.Playlists.GetPlaylists,
    request: {
      account_id: globalState.account.id!,
      ids: [+id],
      exclude_tags: [],
      unpublished_allowed: globalState.user.isSuperAdmin ? true : false,
    },
    onSuccess: (response: { code: string; data: PlaylistResponse[] }) => {
      if (response.code !== "ok" || response.data.length <= 0) return;
      setPlaylist(response.data[0]);
      setLoading(false);
    },
  });

  const { call: getPlaylistResults } = useApi<typeof Api.Playlists.GetPlaylistResults>({
    api: Api.Playlists.GetPlaylistResults,
    request: {
      user_id: globalState.user.id!,
      playlist_id: id,
    },
    onSuccess: (response) => {
      if (response.code !== "ok" || !response.data) return;

      const playlistData = response.data;
      if (!playlistData.playlist_sessions || playlistData.playlist_sessions.length === 0) {
        return;
      }

      // Filter to only include completed sessions
      const completedSessions = playlistData.playlist_sessions.filter((session) => session.play_session.is_completed);

      if (completedSessions.length === 0) {
        return;
      }

      // Sort sessions by created_at in descending order (most recent first)
      const sortedSessions = [...completedSessions].sort((a, b) => {
        const dateA = new Date(a.play_session.created_at).getTime();
        const dateB = new Date(b.play_session.created_at).getTime();
        return dateB - dateA;
      });

      // Find the best result based on byb_iq
      let bestSession = null;
      let highestBybIq = -1;

      for (const session of sortedSessions) {
        if (!session.results) continue;
        const bybIq = session.results.byb_iq ?? -1;
        if (bybIq > highestBybIq) {
          highestBybIq = bybIq;
          bestSession = session;
        }
      }

      // Set the best result for display
      if (bestSession) {
        setBestResult(bestSession);
      }

      // Set current and previous performance stats
      if (sortedSessions.length > 0) {
        const mostRecentSession = sortedSessions[0];
        if (mostRecentSession.results) {
          setCurrentStats({
            awareness_score: mostRecentSession.results.awareness_score ?? 0,
            scan_rate: mostRecentSession.results.scan_rate ?? 0,
            scan_timing: mostRecentSession.results.scan_timing ?? 0,
            total_score: mostRecentSession.results.total_score ?? 0,
            critical_scan_score: mostRecentSession.results.critical_scan_score ?? 0,
            byb_iq: mostRecentSession.results.byb_iq ?? 0,
          });
        }

        // If there's more than one session, get the previous one for comparison
        if (sortedSessions.length > 1) {
          const previousSession = sortedSessions[1];
          if (previousSession.results) {
            setPreviousStats({
              awareness_score: previousSession.results.awareness_score ?? 0,
              scan_rate: previousSession.results.scan_rate ?? 0,
              scan_timing: previousSession.results.scan_timing ?? 0,
              total_score: previousSession.results.total_score ?? 0,
              critical_scan_score: previousSession.results.critical_scan_score ?? 0,
              byb_iq: previousSession.results.byb_iq ?? 0,
            });
          }
        }
      }
    },
  });

  const showVideo = (videoId?: string | null) => {
    if (videoId) {
      state.app.events.showVideo.emit({ videoId });
    }
  };

  const onPlaySessionEventReceived = (event: any) => {
    if (event.action === "complete" && !completeTimeout) {
      completeTimeout = setTimeout(() => {
        // Refresh playlist results when a session is completed
        getPlaylistResults();
        resetTimeout();
      }, 500);
    }
  };

  const resetTimeout = () => {
    if (completeTimeout) {
      clearTimeout(completeTimeout);
      completeTimeout = null;
    }
  };

  useMount(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("playlists.page.title") });
    dispatch({
      type: Actions.ProgressionActions.GetProgressionConfig,
      payload: {
        cache_time: [60, "mins"],
        session_only: true,
        disableLoaders: true,
      },
    });

    getPlaylists();
    getPlaylistResults();
  });

  useEffect(() => {
    resetTimeout();
    Events.app.hideHeaderButton.emit();
    state.app.events.playSessionEvent.uniq(onPlaySessionEventReceived);
    return () => {
      resetTimeout();
      state.app.events.playSessionEvent.remove(onPlaySessionEventReceived);
    };
  }, []);

  return {
    playlist,
    loading,
    showVideo,
    bestResult,
    currentStats,
    previousStats,
  };
};

export default usePlaylistData;
